'use client';

import { useTheme } from 'next-themes';
import { Toaster as Sonner, toast } from 'sonner';

import { cn } from '../lib/utils';

import type React from 'react';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      // A modal overlay sets `pointer-events: none` on body. Inline, so that jsdom tests see it too.
      style={{ pointerEvents: 'auto' }}
      visibleToasts={5}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn([
            // common / utility classes
            'toast group transition group-[.toaster]:[&>[data-icon]>:not(span)]:mr-2 group-[.toaster]:[&>[data-icon]]:m-0 group-[.toaster]:[&>[data-icon]]:w-auto group-[.toaster]:[&>[data-icon]]:p-0',
            // Success styles
            'data-[type=success]:flex data-[type=success]:w-[360px] data-[type=success]:items-center data-[type=success]:rounded-lg data-[type=success]:border data-[type=success]:border-border data-[type=success]:p-3',
            'group-[.toaster]:data-[type=success]:bg-popover group-[.toaster]:data-[type=success]:text-popover-foreground group-[.toaster]:data-[type=success]:drop-shadow-lg',
            // Info styles
            'data-[type=info]:flex data-[type=info]:w-[360px] data-[type=info]:items-center data-[type=info]:rounded-lg data-[type=info]:border data-[type=info]:border-border data-[type=info]:p-3',
            'group-[.toaster]:data-[type=info]:bg-popover group-[.toaster]:data-[type=info]:text-popover-foreground group-[.toaster]:data-[type=info]:drop-shadow-lg',
            // Plain `toast()` styles. A plain toast has no data-type, and a `toast.custom` toast has no data-title.
            '[&:not([data-type]):has([data-title])]:flex [&:not([data-type]):has([data-title])]:w-[360px] [&:not([data-type]):has([data-title])]:items-center [&:not([data-type]):has([data-title])]:rounded-lg [&:not([data-type]):has([data-title])]:border [&:not([data-type]):has([data-title])]:border-border [&:not([data-type]):has([data-title])]:p-3',
            'group-[.toaster]:[&:not([data-type]):has([data-title])]:bg-popover group-[.toaster]:[&:not([data-type]):has([data-title])]:text-popover-foreground group-[.toaster]:[&:not([data-type]):has([data-title])]:drop-shadow-lg',
            // Error styles
            'data-[type=error]:flex data-[type=error]:w-[360px] data-[type=error]:items-center data-[type=error]:rounded-lg data-[type=error]:border data-[type=error]:p-3',
            'group-[.toaster]:data-[type=error]:border-destructive group-[.toaster]:data-[type=error]:bg-destructive group-[.toaster]:data-[type=error]:text-destructive-foreground',
            'group-[.toaster]:[&>button]:data-[type=error]:border-destructive-foreground group-[.toaster]:[&>button]:data-[type=error]:bg-transparent group-[.toaster]:[&>button]:data-[type=error]:text-destructive-foreground',
            'group-[.toaster]:[&>button:hover]:data-[type=error]:border-destructive-foreground group-[.toaster]:[&>button:hover]:data-[type=error]:bg-white/20',
            // Warning styles
            'data-[type=warning]:flex data-[type=warning]:w-[360px] data-[type=warning]:items-center data-[type=warning]:rounded-lg data-[type=warning]:border data-[type=warning]:p-3',
            'group-[.toaster]:data-[type=warning]:border-ring group-[.toaster]:data-[type=warning]:bg-primary group-[.toaster]:data-[type=warning]:text-primary-foreground',
            // Loading styles
            'data-[type=loading]:flex data-[type=loading]:w-[360px] data-[type=loading]:items-center data-[type=loading]:rounded-lg data-[type=loading]:border data-[type=loading]:border-border data-[type=loading]:p-3',
            'group-[.toaster]:data-[type=loading]:bg-popover group-[.toaster]:data-[type=loading]:text-popover-foreground group-[.toaster]:data-[type=loading]:drop-shadow-lg',
          ]),
          title: 'text-sm',
          description: 'text-xs',
          // sonner gives every button `margin-left: auto`, which splits the free space between Cancel and Action.
          actionButton:
            '!ml-2 group-[.toaster]:active:scale-97 rounded group-[.toaster]:border group-[.toaster]:border-solid group-[.toaster]:bg-secondary group-[.toaster]:p-3 group-[.toaster]:text-sm group-[.toaster]:border-input group-[.toaster]:text-foreground',
          cancelButton:
            'group-[.toaster]:active:scale-97 rounded group-[.toaster]:border group-[.toaster]:border-solid group-[.toaster]:bg-transparent group-[.toaster]:p-3 group-[.toaster]:text-sm group-[.toaster]:border-input group-[.toaster]:text-inherit',
        },
      }}
      icons={{
        error: <span />,
        info: <span />,
        success: <span />,
        warning: <span />,
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
